import { Tenants } from '../tenant/tenant';
import endpoint from '../util/endpoint';
import config from '@/config/config';
import { useStores } from '@/lib/hooks/useStores';
import { updateIfCached } from '@/lib/updateIfCached';
import { MasterDataType } from '@/masterData/masterDataList';
import { errorToString } from '@whiz-cart/node-shared/errorToString';
import { StoreGeneralConfig } from '@whiz-cart/node-shared/models/config/storeGeneralConfig';
import { Asset } from '@whiz-cart/node-shared/models/erp/asset';
import { LocalazyCdn } from '@whiz-cart/node-shared/models/localazy/localazyCdn';
import type { OtherString } from '@whiz-cart/node-shared/models/otherString';
import { ParsedDate } from '@whiz-cart/node-shared/models/parsed/parsedDate';
import { Store, UnfinishedStore } from '@whiz-cart/node-shared/models/store';
import { MarketDtoV4Model } from '@whiz-cart/node-shared/models/storeCreation/marketDtoV4Model';
import { Milestone, MilestoneSlim } from '@whiz-cart/node-shared/models/storeCreation/milestone';
import { NavigationStatus, VoucherPromotion } from '@whiz-cart/node-shared/models/storeCreation/milestoneField';
import { StoreWithMilestones } from '@whiz-cart/node-shared/models/storeCreation/storeWithMilestones';
import { service } from '@whiz-cart/node-shared/service/service';
import { request } from '@whiz-cart/ui-shared/request/request';
import { createCache } from 'cross-state';
import _, { keyBy } from 'lodash';
import { useMemo } from 'react';
import { z } from 'zod';

export type StoreTableEntry = z.infer<typeof StoreTableEntry>;

export const OpenHours = z.object({
    opens: z.string(),
    closes: z.string(),
});

export const StoreTableEntry = z.object({
    assets: z.array(Asset).default([]),
    storeGuid: z.string(),
    name: z.string().nullish(),
    tenant: z.string().nullish(),
    externalStoreId: z.string().nullish(),
    storeNumber: z.string().nullish(),
    globalLookupNumber: z.string().nullish(),
    branch: z.string().nullish(),
    startsOn: ParsedDate.nullish(),
    inventoryManagementSystem: z.number().nullish(),
    rwwsMigrationDate: ParsedDate.nullish(),
    configs: z
        .object({
            MPOS_IP_ADDRESS: z.string().nullish(),
            JUMPHOST_IP_ADDRESS: z.string().nullish(),
            HAS_NAVIGATION: z.string().nullish(),
            UI_DEACTIVATE_NAVIGATION: z.string().nullish(),
            UI_DEACTIVATE_LOCATION_BASED_SORTING: z.string().nullish(),
            EASY_DEALS_FLYER_URL: z.string().nullish(),
            ALLOW_ANONYMOUS_LOGIN: z.string().nullish(),
        })
        .nullish(),
    flags: z
        .object({
            enablePosPromotions: z.boolean().nullish(),
            enableVrPayment: z.boolean().nullish(),
            enableDirectDebitPayment: z.boolean().nullish(),
            enableSDD: z.boolean().nullish(),
        })
        .nullish(),
    city: z.string().nullish(),
    zipCode: z.string().nullish(),
    street: z.string().nullish(),
    countryCode: z.string().nullish(),
    storeManagerEmail: z.string().nullish(),
    numberOfCarts: z.number().nullish(),
    cartDeliveryDate: ParsedDate.nullish(),
    hasRamps: z.boolean().nullish(),
    hasSelfCheckout: z.boolean().nullish(),
    hasCashierCheckout: z.boolean().nullish(),
    sosPhoneCount: z.number().nullish(),
    chargingBoxCount: z.number().nullish(),
    chargingStationCount: z.number().nullish(),
    chargingMobileChargersCount: z.number().nullish(),
    chargingSwitchCabinetCount: z.number().nullish(),
    esPremium: z.boolean().nullish(),
    esLight: z.boolean().nullish(),
    expressShopping: z.boolean().nullish(),
    navigationStatus: NavigationStatus.nullish(),
    voucherPromotions: VoucherPromotion.array().nullish(),
    costCenter: z.string().nullish(),
    floorArea: z.number().nullish(),
    cartsSumPlannedRollout: z.number().nullish(),
    cartSumTotal: z.number().nullish(),
    cartSumPremium: z.number().nullish(),
    cartSumLight: z.number().nullish(),
    cartSumModifiedOn: ParsedDate.nullish(),
    fibuStoreName: z.string().nullish(),
    fibuCustomerNumber: z.string().nullish(),
    company: z.string().nullish(),
    operatingMode: z.string().nullish(),

    openingHours: z
        .object({
            monday: OpenHours.nullish(),
            tuesday: OpenHours.nullish(),
            wednesday: OpenHours.nullish(),
            thursday: OpenHours.nullish(),
            friday: OpenHours.nullish(),
            saturday: OpenHours.nullish(),
            sunday: OpenHours.nullish(),
        })
        .nullish(),
});

export enum GetStoreOptionsFlags {
    None = 0,
    VoucherPromotions = 1,
    Assets = 2,
}

export type UnfinishedStoreWithMilestones = z.infer<typeof UnfinishedStoreWithMilestones>;
export const UnfinishedStoreWithMilestones = z.object({
    store: UnfinishedStore,
    milestones: MilestoneSlim.array(),
});

const noRevalidateOnFocus = { invalidateOnWindowFocus: false } as const;

export default service(
    'storeService',
    class StoreService {
        allStores = createCache(async () => {
            const response = await endpoint('storeManager.stores').get();
            const stores = response as Store[];
            return keyBy(stores, 'storeGuid');
        }, noRevalidateOnFocus);

        list = createCache(
            async ({
                skip,
                limit,
                filter,
            }: {
                skip: number;
                limit: number;
                filter?: string;
            }): Promise<{
                items: {
                    store: UnfinishedStore;
                    milestones: MilestoneSlim[];
                }[];
                count: number;
            }> => {
                try {
                    const response = (await endpoint('storeAdmin.listStores', { skip, count: limit }).get({
                        params: { filter },
                    })) as unknown;
                    const { items, count } = z
                        .object({
                            items: UnfinishedStoreWithMilestones.array(),
                            count: z.number(),
                        })
                        .parse(response);

                    return { items, count };
                } catch (e) {
                    console.error('Failed to list stores', errorToString(e));
                    throw e;
                }
            },
            noRevalidateOnFocus,
        );

        listAll = createCache(async () => {
            const response: Record<string, any> = await endpoint('storeAdmin.listAllStores').post(
                [
                    'WELCOME_STORE_NAME',
                    'MPOS_IP_ADDRESS',
                    'JUMPHOST_IP_ADDRESS',
                    'HAS_NAVIGATION',
                    'UI_DEACTIVATE_NAVIGATION',
                    'UI_DEACTIVATE_LOCATION_BASED_SORTING',
                    'EASY_DEALS_FLYER_URL',
                    'ALLOW_ANONYMOUS_LOGIN',
                ],
                { params: { flags: GetStoreOptionsFlags.VoucherPromotions | GetStoreOptionsFlags.Assets } },
            );
            const stores = StoreTableEntry.array().parse(Object.values(response));

            for (const store of stores) {
                const hasNavigation = store.configs?.HAS_NAVIGATION?.toLowerCase() === 'true';
                const uiDeactivateNavigation = store.configs?.UI_DEACTIVATE_NAVIGATION?.toLowerCase() === 'true';
                const uiDeactivateLocationBasedSorting = store.configs?.UI_DEACTIVATE_LOCATION_BASED_SORTING?.toLowerCase() === 'true';

                store.navigationStatus ??=
                    hasNavigation && !uiDeactivateNavigation && !uiDeactivateLocationBasedSorting
                        ? 'active'
                        : hasNavigation && uiDeactivateNavigation && uiDeactivateLocationBasedSorting
                          ? 'internal'
                          : 'off';
            }

            return stores;
        }, noRevalidateOnFocus);

        get = createCache(async (storeGuid: string) => {
            try {
                const response = (await endpoint('storeAdmin.getStore', { storeGuid }).get()) as unknown;
                return StoreWithMilestones.parse(response);
            } catch (e) {
                console.error('Failed to get store', errorToString(e));
                throw e;
            }
        }, noRevalidateOnFocus);

        searchStoresForImport = createCache(async (query: { name?: string; city?: string; zipCode?: string; gln?: string }) => {
            try {
                const response = await endpoint('storeAdmin.searchImportStores').post(query);
                return MarketDtoV4Model.passthrough().array().parse(response);
            } catch (e) {
                console.error('Failed to search stores for import', errorToString(e));
                throw e;
            }
        }, noRevalidateOnFocus);

        async create(draft: { name: string; startsOn: string; storeId: string }) {
            const response = await endpoint('storeAdmin.createStore').post(draft);
            const store = StoreWithMilestones.parse(response);

            this.list.clearAll();
            this.listAll.clearAll();
            this.get(store.store.storeGuid).updateValue(store);
            this.allStores.invalidateAll();

            return store;
        }

        async importStore({ gln, ...body }: { gln: string; startsOn: string; tenant: string }) {
            const response = await endpoint('storeAdmin.importStore', { gln }).post(body);
            const storeGuid = z.string().parse(response);

            this.list.clearAll();
            this.listAll.clearAll();
            this.get(storeGuid).clear();
            this.allStores.invalidateAll();

            return storeGuid;
        }

        async setStatus(storeGuid: string, isActive: boolean) {
            const status = isActive ? 'active' : 'inactive';
            const response = await endpoint('storeAdmin.setStoreStatus', { storeGuid, status }).post();
            const updatedStore = Store.parse(response);

            this.list.clearAll();
            this.listAll.clearAll();
            updateIfCached(this.get(storeGuid), (store) => ({ ...store, store: updatedStore }));

            return updatedStore;
        }

        async runMilestone(storeGuid: string, milestoneId: string, data?: { [key: string]: any }) {
            const response = (await endpoint('storeAdmin.runMilestone', { storeGuid, milestoneId }).post(data, {
                headers: { 'content-type': 'application/json' },
            })) as unknown;
            const {
                store: updatedStore,
                milestones: [updatedMilestone],
            } = z
                .object({
                    store: Store,
                    milestones: z.tuple([Milestone]),
                })
                .parse(response);

            this.list.clearAll();
            this.listAll.clearAll();
            updateIfCached(this.get(storeGuid), (store) => ({
                ...store,
                store: updatedStore,
                milestones: store.milestones.map((x) => (x.milestoneId === milestoneId ? updatedMilestone : x)),
            }));

            return updatedMilestone;
        }

        getStoreMasterData = createCache(async (type: MasterDataType | OtherString) => {
            const response = await endpoint('storeAdmin.getStoreMasterData', { type }).get();
            const parsed = Tenants.parse(response);
            return parsed;
        }, noRevalidateOnFocus);

        /**
         * The backend overwrites `value` and `tenants` on every save, so a rename has to send the stored ones back.
         * `tenant` names a client the user holds the write claim for; without a global role the claim is only checked for it.
         */
        async addOrupdateStoreMasterData(
            data: {
                name: string;
                id: string;
                type: MasterDataType | OtherString;
                value: string | undefined;
                tenants: string[] | undefined;
            },
            tenant?: string,
        ) {
            updateIfCached(this.getStoreMasterData(data.type), (items) =>
                items.map((item: any) => (data.id == item.id ? { ...item, name: data.name, tenants: data.tenants } : item)),
            );
            try {
                return await endpoint('storeAdmin.addOrupdateStoreMasterData').post(data, tenant ? { params: { tenant } } : undefined);
            } finally {
                // Also after a failed save, so the list drops the values written into the cache above.
                this.getStoreMasterData(data.type).invalidate();
            }
        }

        async deleteStoreMasterData(id: string) {
            const response = await endpoint('storeAdmin.deleteStoreMasterData', { id: id }).delete();
            this.getStoreMasterData.invalidateAll();
            return response;
        }

        async runAllMilestones(storeGuid: string) {
            const response = await endpoint('storeAdmin.runAllMilestones', { storeGuid }).get();
            const store = StoreWithMilestones.parse(response);
            this.list.clearAll();
            this.get(store.store.storeGuid).updateValue(store);
        }

        getConfig = createCache(async (storeGuid?: string): Promise<StoreGeneralConfig> => {
            let response;
            if (storeGuid) {
                response = await endpoint('storeManager.getStoreConfig', { storeGuid }).get();
            } else {
                response = await endpoint('storeManager.getGlobalConfig').get();
            }

            return StoreGeneralConfig.parse(response);
        }, noRevalidateOnFocus);

        getCartLanguages = createCache(async (localazyCdn: string) => {
            const response = await request.get(localazyCdn);
            const parsed = LocalazyCdn.parse(response);
            const result = Object.values(parsed.files)?.[0]?.locales;
            return result;
        }, noRevalidateOnFocus);

        useCartUiLanguages() {
            const localazyCdn = config.localazyCdn;
            const [languages] = this.getCartLanguages(localazyCdn).useCache();
            return languages;
        }

        useStoreLookup() {
            const { stores } = useStores();
            return useMemo(() => _.keyBy(Object.values(stores), 'storeGuid'), [stores]);
        }
    },
);
